import { useEffect, useState, useCallback, useMemo } from "react";
import { Search, Filter, X } from "lucide-react";
import api from "@/lib/api";
import { fmtDate } from "@/lib/date";
import { bucketBadgeClass } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";

const BUCKETS = ["all", "0-30", "31-60", "61-90", "91-180", "180+"];


export default function Receivables() {
  const [source, setSource] = useState("all");
  const [bucket, setBucket] = useState("all");
  const [search, setSearch] = useState("");
  const [currency, setCurrency] = useState("all");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [rows, setRows] = useState(null);

  const load = useCallback(async () => {
    setRows(null);
    const params = {};
    if (source !== "all") params.source = source;
    if (bucket !== "all") params.bucket = bucket;
    if (search) params.search = search;
    const { data } = await api.get("/invoices", { params });
    setRows(data);
  }, [source, bucket, search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const currencies = useMemo(
    () => Array.from(new Set((rows || []).map((r) => r.currency).filter(Boolean))).sort(),
    [rows]
  );

  const filtered = useMemo(() => {
    let out = rows || [];
    if (currency !== "all") out = out.filter((r) => r.currency === currency);
    if (overdueOnly) out = out.filter((r) => r.days_overdue > 0);
    return out;
  }, [rows, currency, overdueOnly]);

  const hasFilters = source !== "all" || bucket !== "all" || search || currency !== "all" || overdueOnly;
  const clearAll = () => { setSource("all"); setBucket("all"); setSearch(""); setCurrency("all"); setOverdueOnly(false); };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Receivables</h1>
          <p className="text-muted-foreground mt-1 text-sm">All open invoices across domestic and export ledgers.</p>
        </div>
        {rows && (
          <Badge variant="outline" className="text-xs" data-testid="receivables-count">
            {filtered.length} of {rows.length} invoices
          </Badge>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Tabs value={source} onValueChange={setSource}>
          <TabsList>
            <TabsTrigger value="all" data-testid="receivables-tab-all">All</TabsTrigger>
            <TabsTrigger value="domestic" data-testid="receivables-tab-domestic">Domestic</TabsTrigger>
            <TabsTrigger value="export" data-testid="receivables-tab-export">Export</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
                 placeholder="Search customer or invoice…" className="pl-9"
                 data-testid="receivables-search-input" />
        </div>
        <Select value={currency} onValueChange={setCurrency}>
          <SelectTrigger className="w-[130px]" data-testid="receivables-currency-filter">
            <SelectValue placeholder="Currency" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All currencies</SelectItem>
            {currencies.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <button
          onClick={() => setOverdueOnly((v) => !v)}
          data-testid="receivables-overdue-filter"
          className={`px-3 py-2 rounded-md text-xs font-medium border transition-colors ${
            overdueOnly ? "bg-rose-500 text-white border-rose-500" : "border-border text-muted-foreground hover:bg-muted"
          }`}>
          Overdue only
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Filter className="w-4 h-4 text-muted-foreground mr-1" />
        {BUCKETS.map((b) => (
          <button key={b} onClick={() => setBucket(b)}
            data-testid={`bucket-filter-${b}`}
            className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${
              bucket === b ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-muted"
            }`}>
            {b === "all" ? "All ages" : `${b}d`}
          </button>
        ))}
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={clearAll} className="h-7 text-xs text-muted-foreground" data-testid="receivables-clear-filters">
            <X className="w-3 h-3 mr-1" /> Clear
          </Button>
        )}
      </div>

      <Card className="overflow-hidden" data-testid="receivables-table">
        {!rows ? (
          <div className="p-6 space-y-3">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground">No invoices match your filters.</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50 hover:bg-muted/50">
                  <TableHead className="text-xs uppercase tracking-wider">Invoice</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Customer</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Type</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider whitespace-nowrap">Invoice Date</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider whitespace-nowrap">Due Date</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right whitespace-nowrap">Overdue Days</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-center">Ageing</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((r) => (
                  <TableRow key={r.id} className="hover:bg-muted/40 transition-colors" data-testid={`invoice-row-${r.invoice_no}`}>
                    <TableCell className="font-mono text-xs whitespace-nowrap">{r.invoice_no}</TableCell>
                    <TableCell className="font-medium min-w-[160px] max-w-[240px]">
                      <span className={`break-words ${r.excluded ? "line-through text-muted-foreground" : ""}`}>{r.customer_name}</span>
                      <div className="text-[11px] text-muted-foreground break-words">{r.customer_email || "no email"}</div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={r.source === "domestic"
                        ? "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20"
                        : "bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20"}>
                        {r.source}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{fmtDate(r.invoice_date)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{fmtDate(r.due_date)}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {r.days_overdue > 0
                        ? <span className="font-mono text-sm font-semibold text-rose-600 dark:text-rose-400">{r.days_overdue}d</span>
                        : <span className="text-xs text-emerald-600 dark:text-emerald-400">Not due</span>}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className={bucketBadgeClass(r.bucket)}>{r.bucket}</Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono font-semibold whitespace-nowrap">{r.amount_display}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
